import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ds';
import api from '../../api/client';
import './CreativeEditor.css';

// --- meta_list loader ---
let cachedMetaList = null;

async function loadMetaList() {
  if (cachedMetaList) return cachedMetaList;
  const resp = await fetch(
    'https://man.vm5apis.com/dist/pmp_v3/development/VM5Series/meta_list.js'
  );
  const text = await resp.text();
  const jsonStr = text
    .replace(/^export\s+const\s+meta_list\s*=\s*/, '')
    .replace(/;\s*$/, '');
  // eslint-disable-next-line no-new-func
  const fn = new Function('return ' + jsonStr);
  cachedMetaList = fn();
  console.log('[CreativeEditor] meta_list loaded, components:', Object.keys(cachedMetaList));
  return cachedMetaList;
}

// --- template_textfield parser ---
// The template_textfield is JS code like:
//   const creativeConfig = { "properties": {}, "template": { ... } };
//   export default creativeConfig;
// It contains arrow functions like: "_onPrepared": (x) => { context.comp.x = x; }
// and colon-prefixed strings like: "context": ":context", "adbox": ":basic_adbox"
function parseTemplateTextfield(textfield) {
  if (!textfield) {
    console.warn('[CreativeEditor] parseTemplateTextfield: empty textfield');
    return null;
  }
  try {
    let code = textfield;
    console.log('[CreativeEditor] parseTemplateTextfield: input length =', code.length);

    // Step 1: Remove const/export wrapper
    code = code.replace(/const\s+creativeConfig\s*=\s*/, '');
    code = code.replace(/export\s+default\s+creativeConfig;?\s*$/, '');

    // Step 2: Remove arrow function properties like "_onPrepared": (x) => { ... }
    // and any other function properties that contain arrow functions
    // These can have nested content so we use a balanced brace approach
    code = removeArrowFunctions(code);

    // Step 3: Clean up trailing/double commas left by removal
    code = code.replace(/,\s*,/g, ',');  // Remove double commas
    code = code.replace(/,(\s*[}\]])/g, '$1');  // Remove trailing commas

    code = code.trim();
    if (code.endsWith(';')) code = code.slice(0, -1);

    console.log('[CreativeEditor] parseTemplateTextfield: cleaned code (first 200 chars):', code.substring(0, 200));

    // Step 4: Parse as JSON — the template_textfield uses double-quoted strings
    // which is valid JSON after removing functions
    let config;
    try {
      config = JSON.parse(code);
    } catch (jsonErr) {
      // Fallback: try eval with Function
      console.log('[CreativeEditor] JSON.parse failed, trying Function eval:', jsonErr.message);
      // eslint-disable-next-line no-new-func
      const fn = new Function('const context = { comp: {} }; return ' + code);
      config = fn();
    }

    console.log('[CreativeEditor] parseTemplateTextfield: success, template keys =',
      config?.template ? Object.keys(config.template) : 'NO TEMPLATE');
    return config;
  } catch (e) {
    console.error('[CreativeEditor] parseTemplateTextfield FAILED:', e);
    console.error('[CreativeEditor] Raw textfield:', textfield.substring(0, 300));
    return null;
  }
}

// Remove arrow function properties from JSON-like code
// Strategy: replace entire "key": (args) => { ... } blocks with empty string
// This handles both "_onPrepared": (x) => { ... } and multi-line functions
function removeArrowFunctions(code) {
  // Pattern explanation:
  // "[^"]+"\s*:\s* — match "key":
  // (?:\([^)]*\)|\w+) — match (args) or bareword
  // \s*=>\s* — match =>
  // \{[^{}]*(?:\{[^{}]*\}[^{}]*)*\} — match balanced braces (nested up to 2 levels)
  // This regex handles most common arrow function patterns in template_textfield
  
  // First pass: remove simple arrow functions (no nested braces)
  let result = code.replace(
    /"[^"]+"\s*:\s*(?:\([^)]*\)|\w+)\s*=>\s*\{[^{}]*\}\s*,?/g,
    ''
  );
  
  // Second pass: remove arrow functions with one level of nesting
  // Match: "key": (args) => { ... { ... } ... }
  result = result.replace(
    /"[^"]+"\s*:\s*(?:\([^)]*\)|\w+)\s*=>\s*\{(?:[^{}]|\{[^{}]*\})*\}\s*,?/g,
    ''
  );
  
  return result;
}

// --- Property categorisation ---
import { CATEGORY_ORDER, CATEGORY_CONFIG } from '../../config/categoryConfig';

// (CategoryLabel removed — section header now renders icon box + title + subtitle inline)

function categorisePropertyWithConfig(prop, fieldConfig) {
  // 1. field_config override (from Admin)
  if (fieldConfig?.category) return fieldConfig.category;
  // 2. meta_list category
  return categorisePropertyDefault(prop);
}

function categorisePropertyDefault(prop) {
  if (prop.category) {
    const cat = prop.category;
    // Accept both original lowercase and new capitalized forms
    const normalized = cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
    if (CATEGORY_ORDER.includes(normalized)) return normalized;
    if (CATEGORY_ORDER.includes(cat)) return cat;
  }
  const t = (prop.type || '').toLowerCase();
  const key = (prop.key || '').toLowerCase();
  if (t === 'image' || t === 'video' || t === 'medias') return 'Medias';
  if (t === 'form' || key.includes('track') || key.includes('pixel')) return 'Trackings';
  if (key.includes('url') || key.includes('link')) return 'URLs';
  return 'Advance';
}

// Extract the raw "template: { ... }" block from template_textfield JS code,
// preserving arrow functions intact. Combines with JSON-serialized properties
// to produce the final config_object string for PAC.
function buildConfigObject(properties, rawTextfield) {
  if (!rawTextfield) {
    // Fallback: no template available, return properties only
    return JSON.stringify({ properties });
  }

  // Find "template": { ... } in the raw textfield using balanced brace matching
  // The raw textfield looks like: const creativeConfig = { "properties": {...}, "template": {...} }; export default ...
  const templateKeyIdx = rawTextfield.indexOf('"template"');
  if (templateKeyIdx === -1) {
    console.warn('[buildConfigObject] No "template" key found in raw textfield');
    return JSON.stringify({ properties });
  }

  // Find the opening brace after "template":
  const colonIdx = rawTextfield.indexOf(':', templateKeyIdx + 10);
  const braceStart = rawTextfield.indexOf('{', colonIdx);
  if (braceStart === -1) {
    console.warn('[buildConfigObject] No opening brace for template');
    return JSON.stringify({ properties });
  }

  // Balance braces to find the end of template object
  let depth = 0;
  let braceEnd = -1;
  for (let i = braceStart; i < rawTextfield.length; i++) {
    if (rawTextfield[i] === '{') depth++;
    else if (rawTextfield[i] === '}') {
      depth--;
      if (depth === 0) {
        braceEnd = i;
        break;
      }
    }
  }

  if (braceEnd === -1) {
    console.warn('[buildConfigObject] Unbalanced braces in template');
    return JSON.stringify({ properties });
  }

  // Extract raw template JS (with arrow functions preserved)
  const rawTemplateBlock = rawTextfield.substring(braceStart, braceEnd + 1);

  // Build final config: JSON properties + raw template JS
  const propertiesJSON = JSON.stringify(properties);
  return `{"properties":${propertiesJSON},"template":${rawTemplateBlock}}`;
}

// Build preview config for PAC preview.html postMessage.
// Arrow functions must be STRING values (not real functions).
// Every component needs "context": ":context".
// Every template key needs a corresponding properties entry.
function buildPreviewConfig(properties, rawTextfield) {
  if (!rawTextfield) return { properties, template: {} };

  // Parse the textfield to get the template structure (with real arrow functions)
  const parsed = parseTemplateTextfield(rawTextfield);
  if (!parsed || !parsed.template) return { properties, template: {} };

  // Also extract arrow functions from raw text as strings
  // Strategy: re-parse the raw textfield but keep arrow functions as strings
  const template = {};

  // Get the raw template block from textfield
  const templateKeyIdx = rawTextfield.indexOf('"template"');
  if (templateKeyIdx === -1) return { properties, template: {} };
  const colonIdx = rawTextfield.indexOf(':', templateKeyIdx + 10);
  const braceStart = rawTextfield.indexOf('{', colonIdx);
  if (braceStart === -1) return { properties, template: {} };

  // For each component in parsed template, rebuild with string callbacks
  for (const [compKey, compDef] of Object.entries(parsed.template)) {
    const entry = {};

    // Copy non-function properties from parsed template
    for (const [k, v] of Object.entries(compDef)) {
      if (k.startsWith('_on')) continue; // skip, will extract from raw text
      entry[k] = v;
    }

    // Ensure context
    if (!entry.context) entry.context = ':context';

    // Extract _onXxx callbacks as strings from raw textfield
    // Find this component's block in the raw text
    const compKeyPattern = `"${compKey}"`;
    let compIdx = rawTextfield.indexOf(compKeyPattern, templateKeyIdx);
    if (compIdx !== -1) {
      const compBraceStart = rawTextfield.indexOf('{', compIdx);
      if (compBraceStart !== -1) {
        // Find matching close brace
        let depth = 0;
        let compBraceEnd = -1;
        for (let i = compBraceStart; i < rawTextfield.length; i++) {
          if (rawTextfield[i] === '{') depth++;
          else if (rawTextfield[i] === '}') {
            depth--;
            if (depth === 0) { compBraceEnd = i; break; }
          }
        }
        if (compBraceEnd !== -1) {
          const compBlock = rawTextfield.substring(compBraceStart, compBraceEnd + 1);
          // Extract _onXxx patterns: "_onSomething": (args) => { ... }
          const callbackRegex = /"(_on\w+)"\s*:\s*((?:\([^)]*\)|\w+)\s*=>\s*\{)/g;
          let match;
          while ((match = callbackRegex.exec(compBlock)) !== null) {
            const cbName = match[1];
            const cbStart = match.index + match[0].indexOf(match[2]);
            // Find the arrow function body (balance braces)
            const arrowStart = compBlock.indexOf('=>', match.index);
            const bodyStart = compBlock.indexOf('{', arrowStart);
            let d = 0;
            let bodyEnd = -1;
            for (let i = bodyStart; i < compBlock.length; i++) {
              if (compBlock[i] === '{') d++;
              else if (compBlock[i] === '}') {
                d--;
                if (d === 0) { bodyEnd = i; break; }
              }
            }
            if (bodyEnd !== -1) {
              // Extract full arrow function as string
              const arrowIdx = compBlock.indexOf(match[2], match.index);
              const fullCallback = compBlock.substring(arrowIdx, bodyEnd + 1).trim();
              entry[cbName] = fullCallback;
            }
          }
        }
      }
    }

    // Default _onPrepared if not found: store self in context
    if (!entry._onPrepared) {
      entry._onPrepared = `(${compKey}) => { context.comp.${compKey} = ${compKey}; }`;
    }

    template[compKey] = entry;
  }

  // Ensure every template key has a properties entry
  const fullProperties = { ...properties };
  for (const key of Object.keys(template)) {
    if (!fullProperties[key]) fullProperties[key] = {};
  }

  return { template, properties: fullProperties };
}

// Parse existing creative config_object to extract properties values
function parseExistingConfig(configObject) {
  if (!configObject) return null;
  try {
    // config_object may be JSON or JS (with arrow functions)
    let config;
    try {
      config = JSON.parse(configObject);
    } catch {
      // Try removing JS wrapper: "const creativeConfig = {...}; export default creativeConfig;"
      let code = configObject
        .replace(/^const\s+creativeConfig\s*=\s*/, '')
        .replace(/;\s*export\s+default\s+creativeConfig;?\s*$/, '');
      code = removeArrowFunctions(code);
      code = code.replace(/,\s*,/g, ',').replace(/,(\s*[}\]])/g, '$1').trim();
      if (code.endsWith(';')) code = code.slice(0, -1);
      config = JSON.parse(code);
    }
    return config?.properties || null;
  } catch (e) {
    console.error('[parseExistingConfig] failed:', e);
    return null;
  }
}

// (categoriseProperty moved above as categorisePropertyWithConfig / categorisePropertyDefault)

export default function CreativeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [ad, setAd] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateUUID, setSelectedTemplateUUID] = useState('');
  const [metaList, setMetaList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Component properties: { [componentKey]: { componentName, properties: [...] } }
  const [components, setComponents] = useState({});
  // Raw template JS text from template_textfield (preserves arrow functions for save)
  const [rawTemplateText, setRawTemplateText] = useState('');
  // JSON-safe parsed template (arrow functions stripped, for preview postMessage)
  const [templateConfigForPreview, setTemplateConfigForPreview] = useState(null);
  // Form values: { [componentKey]: { [propertyKey]: value } }
  const [formValues, setFormValues] = useState({});
  // Upload state: { [`${compKey}-${propKey}`]: 'uploading' | null }
  const [uploadingFields, setUploadingFields] = useState({});
  // Hidden values (from template_overrides): same structure as formValues
  const [hiddenValues, setHiddenValues] = useState({});
  // Accordion open state — from CATEGORY_CONFIG defaultOpen
  const [openSections, setOpenSections] = useState(() => {
    const state = {};
    for (const key of CATEGORY_ORDER) {
      state[key] = CATEGORY_CONFIG[key].defaultOpen;
    }
    return state;
  });

  const metaListRef = useRef(null);

  // Lock body scroll — editor uses fixed positioning for full viewport control
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Load ad + templates + meta_list
  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        const [adData, tplData, ml] = await Promise.all([
          api.getPortalAd(id),
          api.getPortalTemplates(id),
          loadMetaList(),
        ]);
        console.log('[CreativeEditor] init: ad =', adData?.name, ', templates count =', tplData?.length);
        console.log('[CreativeEditor] init: templates =', tplData?.map(t => ({
          uuid: t.uuid,
          name: t.name,
          has_textfield: !!t.template_textfield,
          textfield_len: t.template_textfield?.length || 0,
        })));
        setAd(adData);
        setTemplates(tplData);
        setMetaList(ml);
        metaListRef.current = ml;

        // If ad already has a creative, load existing config and auto-select template
        if (adData.creative_uuid && adData.template_uuid) {
          try {
            const creative = await api.getPortalCreative(id);
            if (creative.has_creative && creative.config_object) {
              // Auto-select template (triggers handleTemplateChange via useEffect below)
              setSelectedTemplateUUID(adData.template_uuid);

              // Parse existing config_object to restore form values
              const existingConfig = parseExistingConfig(creative.config_object);
              if (existingConfig) {
                // Store for later — will be applied after handleTemplateChange sets up components
                window.__pendingCreativeValues = existingConfig;
              }
            }
          } catch (e) {
            console.log('[CreativeEditor] Could not load existing creative:', e.message);
          }
        }
      } catch (err) {
        console.error('[CreativeEditor] init failed:', err);
        setError(err.message || '載入失敗');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id]);

  // When template is selected, parse components and build form
  const handleTemplateChange = useCallback(
    (uuid) => {
      setSelectedTemplateUUID(uuid);
      if (!uuid) {
        setComponents({});
        setRawTemplateText('');
        setTemplateConfigForPreview(null);
        setFormValues({});
        setHiddenValues({});
        return;
      }

      const tpl = templates.find((t) => t.uuid === uuid);
      if (!tpl) {
        console.warn('[CreativeEditor] Template not found for uuid:', uuid);
        return;
      }

      const ml = metaListRef.current || metaList;
      const textfield = tpl.template_textfield || '';
      const fieldConfig = tpl.field_config || {}; // from Admin settings
      console.log('[CreativeEditor] handleTemplateChange: uuid =', uuid,
        ', template_textfield length =', textfield.length,
        ', has meta_list =', !!ml);

      if (!textfield) {
        console.warn('[CreativeEditor] No template_textfield for template:', tpl.name);
        console.log('[CreativeEditor] Template object keys:', Object.keys(tpl));
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        return;
      }

      const config = parseTemplateTextfield(textfield);

      if (!config) {
        console.error('[CreativeEditor] parseTemplateTextfield returned null');
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        return;
      }

      if (!config.template) {
        console.error('[CreativeEditor] Parse result has no config.template');
        console.error('[CreativeEditor] config keys:', Object.keys(config));
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        return;
      }

      // Store raw textfield for save (preserves arrow functions)
      setRawTemplateText(textfield);
      // Store JSON-safe template for preview (arrow functions stripped by parseTemplateTextfield)
      setTemplateConfigForPreview(config.template);

      if (!ml) {
        console.error('[CreativeEditor] meta_list not loaded!');
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        return;
      }
      
      console.log('[CreativeEditor] meta_list keys:', Object.keys(ml).slice(0, 10), '...');

      const newComponents = {};
      const newValues = {};
      const newHidden = {};

      // Components are defined in config.template
      // Each entry: { componentName: "VM5xxx", template_overrides: {...}, ... }
      for (const [compKey, compDef] of Object.entries(config.template)) {
        const componentName = compDef.componentName || '';
        const templateOverrides = compDef.template_overrides || {};

        // Look up component in meta_list by componentName
        const metaDef = ml[componentName];
        if (!metaDef) {
          console.log('[CreativeEditor] Component not in meta_list (skipping):', componentName, '(key:', compKey, ')');
          continue;
        }

        // Check if metaDef has any properties (VM5Preload is empty: {})
        const metaKeys = Object.keys(metaDef);
        if (metaKeys.length === 0) {
          // Component has no properties in meta_list, but may have template_overrides
          // Check if there are hidden values to store
          const compHidden = {};
          let hasHiddenValues = false;
          
          for (const [propKey, override] of Object.entries(templateOverrides)) {
            if (override.display === 'hidden' || override.value !== undefined) {
              compHidden[propKey] = override.value !== undefined ? override.value : '';
              hasHiddenValues = true;
            }
          }
          
          if (hasHiddenValues) {
            console.log('[CreativeEditor] Component has no properties but has hidden values:', componentName, Object.keys(compHidden));
            newHidden[compKey] = compHidden;
          } else {
            console.log('[CreativeEditor] Component has no properties and no hidden values (skipping):', componentName);
          }
          continue;
        }

        console.log('[CreativeEditor] Processing component:', compKey, '→', componentName,
          ', props:', metaKeys, ', overrides:', Object.keys(templateOverrides));

        const properties = [];
        const compValues = {};
        const compHidden = {};

        // meta_list structure: { VM5Component: { propKey: { type, format, ... }, ... } }
        for (const [propKey, propDef] of Object.entries(metaDef)) {
          const override = templateOverrides[propKey] || {};

          // Hidden fields: store value but don't render
          // Priority: template_overrides (RD locked) > Admin field_config > default
          if (override.display === 'hidden') {
            compHidden[propKey] = override.value !== undefined ? override.value : propDef.default;
            console.log('[CreativeEditor]   Hidden field (RD locked):', propKey, '=', compHidden[propKey]);
            continue;
          }

          // Admin field_config: check visibility
          const fc = fieldConfig[`${compKey}.${propKey}`];
          if (fc && fc.visible === false) {
            compHidden[propKey] = fc.hidden_value !== undefined && fc.hidden_value !== '' ? fc.hidden_value : propDef.default;
            console.log('[CreativeEditor]   Hidden field (Admin):', propKey, '=', compHidden[propKey]);
            continue;
          }

          const prop = {
            key: propKey,
            label: propDef.label || propKey,
            description: propDef.description || '',
            type: (fc?.type_override) || propDef.type || 'string',
            format: propDef.format || 'string',
            category: fc?.category || propDef.category || '',
            default: override.value !== undefined ? override.value : propDef.default,
            min: propDef.min,
            max: propDef.max,
            options: propDef.options,
            columns: propDef.column,
            columnSize: propDef.column_size,
            accepted: propDef.accepted || [],
            required: !propDef.optional,
            optional: propDef.optional || false,
          };
          properties.push(prop);

          // Set initial value — for image/video, replace fakeimg/placeholder defaults
          let initVal = prop.default !== undefined ? prop.default : getTypeDefault(prop.type, prop.format);
          if (prop.type === 'image' || prop.type === 'video') {
            const isPlaceholderDefault = !initVal || /fakeimg\.pl|placehold|placeholder/i.test(String(initVal));
            if (isPlaceholderDefault) {
              const fcKey = `${compKey}.${propKey}`;
              const fcEntry = fieldConfig[fcKey];
              if (fcEntry?.placeholder_size) {
                initVal = `https://placehold.co/${fcEntry.placeholder_size}?text=${fcEntry.placeholder_size}`;
              } else {
                const defUrl = String(propDef.default || '');
                const m = defUrl.match(/(\d+)x(\d+)/);
                if (m) initVal = `https://placehold.co/${m[1]}x${m[2]}?text=${m[1]}x${m[2]}`;
              }
            }
          }
          compValues[propKey] = initVal;
        }

        if (properties.length > 0) {
          newComponents[compKey] = { componentName, properties };
          newValues[compKey] = compValues;
          if (Object.keys(compHidden).length > 0) {
            newHidden[compKey] = compHidden;
          }
        } else if (Object.keys(compHidden).length > 0) {
          // Component has only hidden fields
          newHidden[compKey] = compHidden;
        }
      }

      console.log('[CreativeEditor] Result: components =', Object.keys(newComponents),
        ', formValues keys =', Object.keys(newValues),
        ', hiddenValues keys =', Object.keys(newHidden));

      if (Object.keys(newComponents).length === 0 && Object.keys(newValues).length === 0) {
        console.warn('[CreativeEditor] ⚠️ No components parsed! This will show raw textarea fallback.');
        console.log('[CreativeEditor] Debug info:');
        console.log('  - config.template entries:', Object.entries(config.template).map(([k, v]) => ({
          key: k,
          componentName: v.componentName,
          hasOverrides: !!v.template_overrides && Object.keys(v.template_overrides).length > 0
        })));
      }

      setComponents(newComponents);

      // If we have pending creative values (from loading existing creative), overlay them
      if (window.__pendingCreativeValues) {
        const existing = window.__pendingCreativeValues;
        delete window.__pendingCreativeValues;
        console.log('[CreativeEditor] Applying existing creative values:', Object.keys(existing));
        for (const [compKey, vals] of Object.entries(existing)) {
          if (newValues[compKey] && typeof vals === 'object' && !Array.isArray(vals)) {
            newValues[compKey] = { ...newValues[compKey], ...vals };
          }
        }
      }

      setFormValues(newValues);
      setHiddenValues(newHidden);
    },
    [templates, metaList]
  );

  // Auto-trigger handleTemplateChange when template is pre-selected from init
  useEffect(() => {
    if (selectedTemplateUUID && templates.length > 0 && Object.keys(components).length === 0) {
      handleTemplateChange(selectedTemplateUUID);
    }
  }, [selectedTemplateUUID, templates, metaList]); // eslint-disable-line

  // Get placeholder URL for image/video fields
  function getPlaceholderUrl(compKey, propKey, metaDef) {
    const tpl = templates.find((t) => t.uuid === selectedTemplateUUID);
    const fc = tpl?.field_config?.[`${compKey}.${propKey}`];
    // 1. Admin setting
    if (fc?.placeholder_size) {
      return `https://placehold.co/${fc.placeholder_size}?text=${fc.placeholder_size}`;
    }
    // 2. Parse from meta_list default URL
    const defaultUrl = metaDef?.default || '';
    const sizeMatch = String(defaultUrl).match(/(\d+)x(\d+)/);
    if (sizeMatch) {
      return `https://placehold.co/${sizeMatch[1]}x${sizeMatch[2]}?text=${sizeMatch[1]}x${sizeMatch[2]}`;
    }
    return null;
  }

  function getTypeDefault(type, format) {
    switch (type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'medias':
        return [];
      case 'form':
        return [];
      case 'image':
      case 'video':
      case 'string':
        // string with format 'array' defaults to empty array
        if (format === 'array') return [];
        return '';
      default:
        return '';
    }
  }

  const updateValue = (compKey, propKey, value) => {
    setFormValues((prev) => ({
      ...prev,
      [compKey]: { ...prev[compKey], [propKey]: value },
    }));
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // --- Preview ---
  const previewIframeRef = useRef(null);
  const previewContainerRef = useRef(null);
  const previewTimerRef = useRef(null);
  const PREVIEW_URL = 'https://man.vm5apis.com/dist/pmp_v3/development/v3.2/preview.html';
  const IFRAME_W = 800;
  const IFRAME_H = 850;

  // Scale iframe to fit container
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;
    const updateScale = () => {
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const scale = Math.min(cw / IFRAME_W, ch / IFRAME_H) * 0.85;
      container.style.setProperty('--preview-scale', scale);
      container.style.setProperty('--preview-w', IFRAME_W + 'px');
      container.style.setProperty('--preview-h', IFRAME_H + 'px');
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [selectedTemplateUUID, rawTemplateText]);
  const PREVIEW_ORIGIN = 'https://man.vm5apis.com/';

  const sendPreview = useCallback(() => {
    const iframe = previewIframeRef.current;
    if (!iframe || !iframe.contentWindow || !rawTemplateText) return;

    const properties = {};
    for (const [compKey, vals] of Object.entries(formValues)) {
      if (compKey === '_raw') continue;
      properties[compKey] = { ...vals };
    }
    for (const [compKey, vals] of Object.entries(hiddenValues)) {
      properties[compKey] = { ...(properties[compKey] || {}), ...vals };
    }

    // Build preview config: arrow functions as STRINGS + context on each component
    const previewConfig = buildPreviewConfig(properties, rawTemplateText);
    iframe.contentWindow.postMessage(
      { creativeConfig: JSON.stringify(previewConfig) },
      PREVIEW_ORIGIN
    );
  }, [formValues, hiddenValues, rawTemplateText]);

  // Debounced preview update on form change
  useEffect(() => {
    if (!rawTemplateText) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(sendPreview, 500);
    return () => clearTimeout(previewTimerRef.current);
  }, [formValues, hiddenValues, sendPreview, rawTemplateText]);

  // --- Upload ---
  const handleFileUpload = async (file, compKey, propKey, mediaIdx) => {
    const fieldKey = mediaIdx !== undefined ? `${compKey}-${propKey}-${mediaIdx}` : `${compKey}-${propKey}`;
    const isImage = /\.(png|jpe?g|gif)$/i.test(file.name);
    const isVideo = /\.(mp4|webm)$/i.test(file.name);
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;

    if (!isImage && !isVideo) {
      addToast('不支援的檔案格式', 'error');
      return;
    }
    if (file.size > maxSize) {
      addToast(`檔案大小超過上限（${isVideo ? '50MB' : '5MB'}）`, 'error');
      return;
    }

    setUploadingFields((prev) => ({ ...prev, [fieldKey]: 'uploading' }));
    try {
      const result = await api.uploadFile(file);
      const cdnUrl = result.resourceUrl;

      if (mediaIdx !== undefined) {
        // Update media item
        const items = [...(formValues[compKey]?.[propKey] || [])];
        items[mediaIdx] = { ...items[mediaIdx], image: cdnUrl };
        updateValue(compKey, propKey, items);
      } else {
        updateValue(compKey, propKey, cdnUrl);
      }
      addToast('上傳成功', 'success');
    } catch (err) {
      addToast('上傳失敗：' + (err.message || '未知錯誤'), 'error');
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldKey]: null }));
    }
  };

  // Build config_object and save
  const handleSave = async () => {
    if (!selectedTemplateUUID) {
      addToast('請先選擇模板', 'error');
      return;
    }

    setSaving(true);
    try {
      // If using raw JSON fallback mode
      if (formValues._raw) {
        const configObject = formValues._raw;
        console.log('[CreativeEditor] handleSave: using raw JSON');
        await api.createPortalCreative(id, {
          template_uuid: selectedTemplateUUID,
          creative_name: (ad?.name || 'Creative') + ' 素材',
          config_object: configObject,
        });
        addToast(ad?.creative_uuid ? '素材更新成功' : '素材建立成功', 'success');
        navigate(`/ads/${id}`);
        return;
      }

      // Build properties: merge visible form values + hidden values
      // Structure: { componentKey: { propKey: value, ... }, ... }
      const properties = {};
      for (const [compKey, vals] of Object.entries(formValues)) {
        properties[compKey] = { ...vals };
      }
      // Merge hidden values
      for (const [compKey, vals] of Object.entries(hiddenValues)) {
        properties[compKey] = { ...(properties[compKey] || {}), ...vals };
      }

      // Build config_object by combining JSON properties with raw template text
      // The raw template text preserves arrow functions that JSON.stringify would lose
      const configObject = buildConfigObject(properties, rawTemplateText);
      console.log('[CreativeEditor] handleSave: config_object =', configObject.substring(0, 200));

      await api.createPortalCreative(id, {
        template_uuid: selectedTemplateUUID,
        creative_name: (ad?.name || 'Creative') + ' 素材',
        config_object: configObject,
      });

      addToast('素材建立成功', 'success');
      navigate(`/ads/${id}`);
    } catch (err) {
      addToast('建立素材失敗：' + (err.message || '未知錯誤'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- Render helpers ---

  function renderField(compKey, prop) {
    const value = formValues[compKey]?.[prop.key];
    const acceptedHint = prop.accepted && prop.accepted.length > 0
      ? prop.accepted.join(', ')
      : '';

    switch (prop.type) {
      case 'image':
        return renderUploadableField(compKey, prop, value, 'image', acceptedHint);

      case 'video':
        return renderUploadableField(compKey, prop, value, 'video', acceptedHint);

      case 'number':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
              {prop.min !== undefined && prop.max !== undefined && (
                <span className="creative-field-hint">
                  ({prop.min} ~ {prop.max})
                </span>
              )}
            </label>
            <input
              type="number"
              value={value ?? ''}
              min={prop.min}
              max={prop.max}
              onChange={(e) => updateValue(compKey, prop.key, Number(e.target.value))}
            />
          </div>
        );

      case 'boolean':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <div className="creative-field-checkbox">
              <input
                type="checkbox"
                id={`${compKey}-${prop.key}`}
                checked={!!value}
                onChange={(e) => updateValue(compKey, prop.key, e.target.checked)}
              />
              <label htmlFor={`${compKey}-${prop.key}`}>
                {prop.description || prop.label}
              </label>
            </div>
          </div>
        );

      case 'color':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
            </label>
            <div className="color-field-row">
              <input
                type="text"
                value={value || ''}
                onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
                placeholder="#000000"
              />
              <div
                className="color-swatch"
                style={{ background: value || '#CCCCCC' }}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'color';
                  input.value = value || '#000000';
                  input.addEventListener('input', (e) => updateValue(compKey, prop.key, e.target.value));
                  input.click();
                }}
              />
            </div>
          </div>
        );

      case 'select':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
            </label>
            <select
              value={value || ''}
              onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
            >
              <option value="">請選擇</option>
              {(prop.options || []).map((opt) => {
                const optVal = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <option key={optVal} value={optVal}>
                    {optLabel}
                  </option>
                );
              })}
            </select>
          </div>
        );

      case 'medias':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
              {(prop.min !== undefined || prop.max !== undefined) && (
                <span className="creative-field-hint">
                  {prop.min !== undefined && prop.max !== undefined
                    ? `${prop.min} ~ ${prop.max} 張`
                    : prop.min !== undefined
                      ? `至少 ${prop.min} 張`
                      : `最多 ${prop.max} 張`}
                </span>
              )}
            </label>
            {renderMediasField(compKey, prop)}
          </div>
        );

      case 'form':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
            </label>
            {renderFormField(compKey, prop)}
          </div>
        );

      case 'string':
      default:
        // Handle string with format: 'array' (e.g. pixel IDs)
        if (prop.format === 'array') {
          return renderStringArrayField(compKey, prop);
        }
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
              {prop.optional && (
                <span className="creative-field-hint">（選填）</span>
              )}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
            />
          </div>
        );
    }
  }

  // Render image/video field with drag-drop upload + URL input
  function renderUploadableField(compKey, prop, value, mediaType, acceptedHint) {
    const fieldKey = `${compKey}-${prop.key}`;
    const isUploading = uploadingFields[fieldKey] === 'uploading';
    const acceptExts = mediaType === 'video' ? '.mp4,.webm' : '.png,.jpg,.jpeg,.gif';

    const handleDrop = (e) => {
      e.preventDefault();
      e.currentTarget.classList.remove('upload-zone--dragover');
      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file, compKey, prop.key);
    };

    return (
      <div className="creative-field" key={fieldKey}>
        <label className="creative-field-label">
          {prop.description || prop.label}
          {acceptedHint && <span className="creative-field-hint">格式：{acceptedHint}</span>}
          {prop.optional && <span className="creative-field-hint">（選填）</span>}
        </label>
        <div
          className={`upload-zone ${isUploading ? 'upload-zone--uploading' : ''}`}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('upload-zone--dragover'); }}
          onDragLeave={(e) => e.currentTarget.classList.remove('upload-zone--dragover')}
          onDrop={handleDrop}
          onClick={() => !isUploading && document.getElementById(`file-${fieldKey}`)?.click()}
        >
          <input
            id={`file-${fieldKey}`}
            type="file"
            accept={acceptExts}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleFileUpload(file, compKey, prop.key);
              e.target.value = '';
            }}
          />
          {isUploading ? (
            <span className="upload-zone__text">上傳中...</span>
          ) : (
            <span className="upload-zone__text">拖拉檔案至此 或 點擊選擇</span>
          )}
        </div>
        <div className="upload-url-row">
          <span className="upload-url-label">或直接輸入 URL：</span>
          <input
            type="url"
            value={value || ''}
            onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
            placeholder="https://..."
            disabled={isUploading}
          />
        </div>
        {value && mediaType === 'image' && (
          <img src={value} alt="preview" className="image-preview"
            onError={(e) => (e.target.style.display = 'none')}
            onLoad={(e) => (e.target.style.display = 'block')} />
        )}
        {!value && mediaType === 'image' && (() => {
          const phUrl = getPlaceholderUrl(compKey, prop.key, prop);
          return phUrl ? (
            <img src={phUrl} alt="placeholder" className="image-preview placeholder-image" />
          ) : null;
        })()}
      </div>
    );
  }

  // Render a string field with format: 'array' (comma-separated input → array)
  function renderStringArrayField(compKey, prop) {
    const value = formValues[compKey]?.[prop.key];
    const displayVal = Array.isArray(value) ? value.join(', ') : (value || '');

    return (
      <div className="creative-field" key={`${compKey}-${prop.key}`}>
        <label className="creative-field-label">
          {prop.description || prop.label}
          <span className="creative-field-hint">（以逗號分隔多個值）</span>
        </label>
        <input
          type="text"
          value={displayVal}
          onChange={(e) => {
            const raw = e.target.value;
            const arr = raw.split(',').map(s => s.trim()).filter(Boolean);
            updateValue(compKey, prop.key, arr);
          }}
          placeholder="例如: value1, value2"
        />
      </div>
    );
  }

  function renderMediasField(compKey, prop) {
    const items = formValues[compKey]?.[prop.key] || [];

    // meta_list column format: [{ colKey1: { type, description, ... }, colKey2: { ... } }]
    // Each element in the array is an object with one or more column definitions
    const columns = parseColumns(prop.columns, [
      { key: 'image', label: '圖片網址', type: 'image' },
      { key: 'click_url', label: '點擊連結', type: 'string' },
    ]);

    const buildEmptyItem = () => {
      const item = {};
      for (const col of columns) {
        let val = col.default || '';
        // For image columns, use placeholder if default is empty or a placeholder URL
        if (col.type === 'image' && (!val || /fakeimg\.pl|placehold|placeholder/i.test(val))) {
          const phUrl = getPlaceholderUrl(compKey, prop.key, prop);
          if (phUrl) val = phUrl;
        }
        item[col.key] = val;
      }
      return item;
    };

    // Ensure minimum items are pre-filled
    if (items.length === 0 && prop.min) {
      const initialItems = [];
      for (let i = 0; i < prop.min; i++) {
        initialItems.push(buildEmptyItem());
      }
      // Defer state update
      setTimeout(() => updateValue(compKey, prop.key, initialItems), 0);
    }

    const addItem = () => {
      if (prop.max && items.length >= prop.max) return;
      updateValue(compKey, prop.key, [...items, buildEmptyItem()]);
    };

    const removeItem = (idx) => {
      if (prop.min && items.length <= prop.min) return;
      updateValue(compKey, prop.key, items.filter((_, i) => i !== idx));
    };

    const updateItem = (idx, field, val) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], [field]: val };
      updateValue(compKey, prop.key, newItems);
    };

    const canRemove = !prop.min || items.length > prop.min;
    const canAdd = !prop.max || items.length < prop.max;

    return (
      <div className="repeatable-group">
        {items.map((item, idx) => (
          <div className="repeatable-item" key={idx}>
            <div className="repeatable-item-fields">
              <div className="repeatable-item-number">{idx + 1}</div>
              <div className="repeatable-item-columns">
                {columns.map((col) => {
                  const colType = (col.type || '').toLowerCase();
                  const isImage = colType === 'image' || col.key.toLowerCase().includes('image');
                  const colAccepted = col.accepted && col.accepted.length > 0
                    ? col.accepted.join(', ') : '';
                  const mediaFieldKey = `${compKey}-${prop.key}-${idx}-${col.key}`;
                  const isMediaUploading = uploadingFields[mediaFieldKey] === 'uploading';
                  return (
                    <div className="repeatable-column" key={col.key}>
                      <label className="repeatable-column-label">
                        {col.label || col.key}
                        {colAccepted && (
                          <span className="creative-field-hint"> ({colAccepted})</span>
                        )}
                      </label>
                      {isImage ? (
                        <>
                          <div
                            className={`upload-zone upload-zone--sm ${isMediaUploading ? 'upload-zone--uploading' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('upload-zone--dragover'); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove('upload-zone--dragover')}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('upload-zone--dragover');
                              const file = e.dataTransfer.files[0];
                              if (file) {
                                setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: 'uploading' }));
                                handleFileUpload(file, compKey, prop.key, idx).finally(() => {
                                  setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: null }));
                                });
                              }
                            }}
                            onClick={() => !isMediaUploading && document.getElementById(`file-${mediaFieldKey}`)?.click()}
                          >
                            <input
                              id={`file-${mediaFieldKey}`}
                              type="file"
                              accept=".png,.jpg,.jpeg,.gif"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: 'uploading' }));
                                  handleFileUpload(file, compKey, prop.key, idx).finally(() => {
                                    setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: null }));
                                  });
                                }
                                e.target.value = '';
                              }}
                            />
                            {isMediaUploading ? '上傳中...' : '拖拉或點擊上傳'}
                          </div>
                          <input
                            type="url"
                            placeholder={col.label || col.key}
                            value={item[col.key] || ''}
                            onChange={(e) => updateItem(idx, col.key, e.target.value)}
                            disabled={isMediaUploading}
                          />
                          {item[col.key] ? (
                            <img
                              src={item[col.key]}
                              alt="preview"
                              className="image-preview-small"
                              onError={(e) => (e.target.style.display = 'none')}
                              onLoad={(e) => (e.target.style.display = 'block')}
                            />
                          ) : (() => {
                            const phUrl = getPlaceholderUrl(compKey, prop.key, prop);
                            return phUrl ? (
                              <img src={phUrl} alt="placeholder" className="image-preview-small placeholder-image" />
                            ) : null;
                          })()}
                        </>
                      ) : (
                        <input
                          type="url"
                          placeholder={col.label || col.key}
                          value={item[col.key] || ''}
                          onChange={(e) => updateItem(idx, col.key, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              className="btn-remove-item"
              onClick={() => removeItem(idx)}
              disabled={!canRemove}
              title={!canRemove ? `至少需要 ${prop.min} 項` : '刪除'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))}
        <button
          className="btn-add-item"
          onClick={addItem}
          disabled={!canAdd}
        >
          + 新增素材{!canAdd ? ` (已達上限 ${prop.max})` : ''}
        </button>
      </div>
    );
  }

  function renderFormField(compKey, prop) {
    const items = formValues[compKey]?.[prop.key] || [];
    const isTracker = prop.key.toLowerCase().includes('track');

    // Parse column definitions from meta_list
    const formColumns = parseColumns(prop.columns, []);

    const addItem = () => {
      const newItem = {};
      if (formColumns.length > 0) {
        for (const col of formColumns) {
          newItem[col.key] = col.default || '';
        }
      } else if (isTracker) {
        Object.assign(newItem, { party: '', event: 'impression', tag_content: '' });
      }
      updateValue(compKey, prop.key, [...items, newItem]);
    };

    const removeItem = (idx) => {
      updateValue(
        compKey,
        prop.key,
        items.filter((_, i) => i !== idx)
      );
    };

    const updateItem = (idx, field, val) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], [field]: val };
      updateValue(compKey, prop.key, newItems);
    };

    return (
      <div className="repeatable-group">
        {items.map((item, idx) => (
          <div className="repeatable-item" key={idx}>
            <div className="repeatable-item-fields">
              <div className="repeatable-item-row">
                {formColumns.length > 0 ? (
                  <>
                    {formColumns.map((col) => (
                      col.type === 'select' && col.options?.length > 0 ? (
                        <select
                          key={col.key}
                          value={item[col.key] || col.default || ''}
                          onChange={(e) => updateItem(idx, col.key, e.target.value)}
                        >
                          <option value="">{col.label}</option>
                          {col.options.map((opt) => (
                            <option key={typeof opt === 'object' ? opt.value : opt}
                                    value={typeof opt === 'object' ? opt.value : opt}>
                              {typeof opt === 'object' ? opt.label : opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          key={col.key}
                          type="text"
                          placeholder={col.label}
                          value={item[col.key] || ''}
                          onChange={(e) => updateItem(idx, col.key, e.target.value)}
                        />
                      )
                    ))}
                  </>
                ) : (
                  // No column definition — render keys from item
                  Object.keys(item).map((itemKey) => (
                    <input
                      key={itemKey}
                      type="text"
                      placeholder={itemKey}
                      value={item[itemKey] || ''}
                      onChange={(e) => updateItem(idx, itemKey, e.target.value)}
                    />
                  ))
                )}
              </div>
            </div>
            <button className="btn-remove-item" onClick={() => removeItem(idx)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))}
        <button className="btn-add-item" onClick={addItem}>
          + 新增{isTracker ? '追蹤碼' : '項目'}
        </button>
      </div>
    );
  }

  // Parse meta_list column definitions into flat array
  // column format: [{ colKey1: { type, description, ... }, colKey2: { ... } }, ...]
  function parseColumns(rawColumns, fallback) {
    if (!rawColumns || !Array.isArray(rawColumns) || rawColumns.length === 0) {
      return fallback;
    }
    const columns = [];
    for (const colGroup of rawColumns) {
      if (typeof colGroup !== 'object') continue;
      for (const [colKey, colDef] of Object.entries(colGroup)) {
        columns.push({
          key: colKey,
          label: colDef.description || colKey,
          type: colDef.type || 'string',
          format: colDef.format || 'string',
          accepted: colDef.accepted || [],
          options: colDef.options || [],
          default: colDef.default || '',
          optional: colDef.optional || false,
        });
      }
    }
    return columns.length > 0 ? columns : fallback;
  }

  // Group all visible properties by category, preserving component grouping
  function getGroupedProperties() {
    const groups = {};
    for (const key of CATEGORY_ORDER) groups[key] = [];

    for (const [compKey, comp] of Object.entries(components)) {
      // Get the template's field_config for this component
      const tpl = templates.find((t) => t.uuid === selectedTemplateUUID);
      const fieldConfig = tpl?.field_config || {};

      for (const prop of comp.properties) {
        const fc = fieldConfig[`${compKey}.${prop.key}`];
        const cat = categorisePropertyWithConfig(prop, fc);
        if (groups[cat]) {
          groups[cat].push({ compKey, componentName: comp.componentName, prop });
        } else {
          groups['Advance'].push({ compKey, componentName: comp.componentName, prop });
        }
      }
    }

    return groups;
  }

  // Group items within a category by componentName for sub-headers
  function groupByComponent(items) {
    const result = [];
    let currentComp = null;

    for (const item of items) {
      if (item.componentName !== currentComp) {
        result.push({ componentName: item.componentName, fields: [item] });
        currentComp = item.componentName;
      } else {
        result[result.length - 1].fields.push(item);
      }
    }

    return result;
  }

  // --- Main render ---

  if (loading) {
    return (
      <div className="creative-editor-page">
        <div className="creative-loading">載入中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="creative-editor-page">
        <div className="creative-error">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate(`/ads/${id}`)}>
            返回廣告詳情
          </button>
        </div>
      </div>
    );
  }

  const grouped = getGroupedProperties();
  const hasComponents = Object.keys(components).length > 0;
  // Check if there are multiple distinct component names across all categories
  const allComponentNames = new Set();
  for (const cat of CATEGORY_ORDER) {
    for (const item of grouped[cat]) {
      if (item.componentName) allComponentNames.add(item.componentName);
    }
  }
  const showComponentHeaders = allComponentNames.size > 1;

  return (
    <div className="creative-editor-page">
      <div className="editor-header">
        <div className="editor-header-left">
          <nav className="breadcrumb">
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/ads'); }}>廣告管理</a>
            <span className="sep">›</span>
            <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/ads/${id}`); }}>
              {ad?.name || `廣告 #${id}`}
            </a>
            <span className="sep">›</span>
            <span className="current">{ad?.creative_uuid ? '編輯素材' : '建立素材'}</span>
          </nav>
        </div>
        <div className="editor-header-right">
          {selectedTemplateUUID && templates.length > 0 && (
            <span className="header-template-info">
              模板：{templates.find((t) => t.uuid === selectedTemplateUUID)?.name || ''}
            </span>
          )}
          <button className="btn btn-md btn-secondary" onClick={() => navigate(`/ads/${id}`)}>取消</button>
          <button className="btn btn-md btn-primary" onClick={handleSave} disabled={saving || !selectedTemplateUUID}>
            {saving ? '儲存中...' : '儲存素材'}
          </button>
        </div>
      </div>

      <div className="creative-editor-layout">
      <div className="creative-editor-content">

        {/* Template selector */}
        <div className="template-selector">
          <label>模板 (Template)</label>
          {templates.length === 0 ? (
            <div className="no-templates-msg">
              此版位類型沒有可用的模板，請聯繫管理員設定。
            </div>
          ) : (
            <select
              value={selectedTemplateUUID}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="">請選擇模板</option>
              {templates.map((tpl) => (
                <option key={tpl.uuid} value={tpl.uuid}>
                  {tpl.name || tpl.uuid}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* If template selected but no parseable components, show raw textarea */}
        {selectedTemplateUUID && !hasComponents && (
          <div className="template-selector">
            <div className="raw-fallback__hint">
              無法解析此模板的欄位結構，請直接編輯 JSON 設定
            </div>
            <label>素材設定 (config_object JSON)</label>
            <textarea
              rows={8}
              value={formValues._raw || '{\n  "properties": {}\n}'}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, _raw: e.target.value }))
              }
              placeholder='{"properties": {}}'
            />
          </div>
        )}

        {/* Grouped sections */}
        {hasComponents && (
          <div className="form-sections">
          {CATEGORY_ORDER.map((cat) => {
            const items = grouped[cat];
            if (!items || items.length === 0) return null;
            const cfg = CATEGORY_CONFIG[cat];
            const componentGroups = groupByComponent(items);
            const isOpen = openSections[cat];

            return (
              <div className={`form-section ${isOpen ? 'open' : ''}`} key={cat}>
                <div className="form-section__header" onClick={() => toggleSection(cat)}>
                  <div className="form-section__icon">{cfg.icon}</div>
                  <div className="form-section__title-group">
                    <div className="form-section__title">
                      {cfg.label}
                      <span className="form-section__count">{items.length} 個欄位</span>
                    </div>
                    <div className="form-section__subtitle">{cfg.subtitle}</div>
                  </div>
                  <div className="form-section__chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>
                {isOpen && (
                  <div className="form-section__body">
                    {componentGroups.map((group) => (
                      <div key={group.componentName} className="component-group">
                        {showComponentHeaders && group.componentName && (
                          <div className="component-group-header">
                            {group.componentName}
                          </div>
                        )}
                        {group.fields.map(({ compKey, prop }) =>
                          renderField(compKey, prop)
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}

        {/* Actions moved to sticky header */}
      </div>

      {/* Preview Panel */}
      {selectedTemplateUUID && rawTemplateText && (
        <div className="creative-preview-panel">
          <div className="creative-preview-panel__header">
            <span>預覽</span>
            <button className="btn btn-text btn-sm" onClick={sendPreview}>重新整理預覽</button>
          </div>
          <div className="creative-preview-content" ref={previewContainerRef}>
            <iframe
              ref={previewIframeRef}
              src={PREVIEW_URL}
              title="Creative Preview"
              className="creative-preview-iframe"
              frameBorder="0"
              onLoad={sendPreview}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
